"use client";

/**
 * RFQ project hooks — mirror of `front-react/src/hooks/useRFQProject.ts`.
 *
 * Adapted to the Next.js port: `useAuth` → `useAuthContext`, and full-project
 * reads use `getFullRFQProject` (the supplier portal keeps the lighter type).
 */
import { useState, useEffect, useCallback } from "react";
import type { RFQProject, WorkflowState, IndustryVertical } from "../types";
import {
  createRFQProject,
  getFullRFQProject,
  getUserRFQProjects,
  getUserRFQProjectsWhereStakeholder,
  getUserRFQProjectsAsSupplier,
  updateRFQData,
  transitionWorkflowState,
  cancelRFQ,
  reopenEvaluation,
  deleteRFQProject,
  rollbackToStep,
} from "@/platform/firebase/firestore/rfq";
import { useAuthContext } from "@/platform/auth/AuthContext";

export function useRFQProject(wid?: string) {
  const { user } = useAuthContext();
  const [project, setProject] = useState<RFQProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch project
  const fetchProject = useCallback(async () => {
    if (!wid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getFullRFQProject(wid);
      setProject(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching RFQ project:", err);
      setError("Failed to load project");
    } finally {
      setLoading(false);
    }
  }, [wid]);

  useEffect(() => {
    void fetchProject();
  }, [fetchProject]);

  // Create project
  const createProject = async (
    projectName: string,
    industry: IndustryVertical,
  ): Promise<string> => {
    if (!user) throw new Error("User not authenticated");

    try {
      return await createRFQProject(user.uid, user.email || "", projectName, industry);
    } catch (err) {
      console.error("Error creating RFQ project:", err);
      throw err;
    }
  };

  // Update data
  const updateData = async (path: string, data: unknown): Promise<void> => {
    if (!wid) throw new Error("No project ID");
    if (!user) throw new Error("User not authenticated");

    try {
      await updateRFQData(wid, path, data);
      await fetchProject(); // Refresh
    } catch (err) {
      console.error("Error updating RFQ data:", err);
      throw err;
    }
  };

  // Transition state
  const transitionState = async (newState: WorkflowState): Promise<void> => {
    if (!wid) throw new Error("No project ID");
    if (!user) throw new Error("User not authenticated");

    try {
      await transitionWorkflowState(wid, newState, user.uid, user.email || "");
      await fetchProject(); // Refresh
    } catch (err) {
      console.error("Error transitioning state:", err);
      throw err;
    }
  };

  // Cancel RFQ
  const cancel = async (reason: string): Promise<void> => {
    if (!wid) throw new Error("No project ID");
    if (!user) throw new Error("User not authenticated");

    try {
      await cancelRFQ(wid, reason, user.uid, user.email || "");
      await fetchProject(); // Refresh
    } catch (err) {
      console.error("Error cancelling RFQ:", err);
      throw err;
    }
  };

  // Reopen evaluation
  const reopen = async (reason: string): Promise<void> => {
    if (!wid) throw new Error("No project ID");
    if (!user) throw new Error("User not authenticated");

    try {
      await reopenEvaluation(wid, reason, user.uid, user.email || "");
      await fetchProject(); // Refresh
    } catch (err) {
      console.error("Error reopening evaluation:", err);
      throw err;
    }
  };

  // Rollback
  const rollback = async (targetStep: number): Promise<void> => {
    if (!wid) throw new Error("No project ID");
    if (!user) throw new Error("User not authenticated");

    try {
      await rollbackToStep(wid, targetStep, user.uid, user.email || "");
      await fetchProject(); // Refresh
    } catch (err) {
      console.error("Error rolling back:", err);
      throw err;
    }
  };

  // Refresh project
  const refreshProject = async (): Promise<void> => {
    await fetchProject();
  };

  return {
    project,
    loading,
    error,
    createProject,
    updateData,
    transitionState,
    cancel,
    reopen,
    rollback,
    refreshProject,
  };
}

export function useUserRFQProjects(userId: string | null, userEmail: string | null) {
  const [projects, setProjects] = useState<RFQProject[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    if (!userId && !userEmail) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const ownedProjects = userId ? await getUserRFQProjects(userId) : [];
      const stakeholderProjects = userEmail
        ? await getUserRFQProjectsWhereStakeholder(userEmail)
        : [];
      const supplierProjects = userEmail
        ? await getUserRFQProjectsAsSupplier(userEmail)
        : [];

      const allProjects = [...ownedProjects] as RFQProject[];
      [...stakeholderProjects, ...supplierProjects].forEach((sp) => {
        if (!allProjects.find((p) => p.wid === sp.wid)) {
          allProjects.push(sp as RFQProject);
        }
      });

      allProjects.sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );

      setProjects(allProjects);
    } catch (err) {
      console.error("Error fetching RFQ projects:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, userEmail]);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  const deleteProject = async (wid: string): Promise<void> => {
    try {
      await deleteRFQProject(wid);
      await fetchProjects(); // Refresh
    } catch (err) {
      console.error("Error deleting RFQ project:", err);
      throw err;
    }
  };

  const refreshProjects = async (): Promise<void> => {
    await fetchProjects();
  };

  return {
    projects,
    loading,
    deleteProject,
    refreshProjects,
  };
}
